import { memo, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';
import type { FreeCropHandle } from '../../../types/freeCrop';
import { useFreeCropLogic } from '../../../hooks/logic/useFreeCropLogic';

type FreeCropCanvasProps = {
  imageUrl: string;
  file: File;
  onChange?: (state: { loading?: boolean }) => void;
};

const FreeCropCanvas = memo(
  forwardRef<FreeCropHandle, FreeCropCanvasProps>(function FreeCropCanvas({ imageUrl, file, onChange }, ref) {
    const {
      containerRef,
      previewCanvasRef,
      loading,
      showPreview,
      isDragging,
      handlePointerDown,
      handlePointerMove,
      handlePointerUp,
      handlePointerLeave,
      preview,
      done,
      reset,
      showPreviewAction,
      hidePreviewAction,
      saveChanges,
      revertToInitial,
      getSavedImage,
    } = useFreeCropLogic({ imageUrl, file, onChange });

    useImperativeHandle(
      ref,
      () => ({
        preview,
        done,
        reset,
        showPreview: showPreviewAction,
        hidePreview: hidePreviewAction,
        saveChanges,
        revertToInitial,
        getSavedImage,
      }),
      [preview, done, reset, showPreviewAction, hidePreviewAction, saveChanges, revertToInitial, getSavedImage],
    );

    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 240,
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <canvas
          ref={previewCanvasRef}
          onPointerDown={showPreview ? undefined : handlePointerDown}
          onPointerMove={showPreview ? undefined : handlePointerMove}
          onPointerUp={showPreview ? undefined : handlePointerUp}
          onPointerLeave={showPreview ? undefined : handlePointerLeave}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'block',
            cursor: showPreview ? 'default' : isDragging ? 'grabbing' : 'default',
          }}
        />
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.3)',
              zIndex: 20,
            }}
          >
            <span style={{ color: 'white', fontSize: 12 }}>Loading...</span>
          </Box>
        )}
      </Box>
    );
  }),
);

FreeCropCanvas.displayName = 'FreeCropCanvas';

export default FreeCropCanvas;
